import React, { useState } from 'react';
import { OptimizationResult } from '../types';
import { PalletVisualization } from './PalletVisualization';
import { ResultsDisplay } from './ResultsDisplay';
import { useNavigate } from 'react-router-dom';

interface VisualizationPageProps {
  result: OptimizationResult | null;
}

export const VisualizationPage: React.FC<VisualizationPageProps> = ({ result }) => {
  const [activeView, setActiveView] = useState<'visualization' | 'details'>('visualization');
  const navigate = useNavigate();

  if (!result) {
    return (
      <div className="text-center py-12">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md mx-auto">
          <h1 className="text-2xl font-bold mb-4">Pallet Visualization</h1>
          <p className="text-gray-600 mb-6">No optimization results to visualize.</p>
          <button
            onClick={() => navigate('/optimization')}
            className="bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Back to Optimization
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Back Button */}
      <div className="mb-4 sm:mb-6">
        <button
          onClick={() => navigate('/optimization')}
          className="inline-flex items-center px-3 sm:px-4 py-2 bg-gray-100 text-gray-700 rounded-lg shadow-sm hover:bg-gray-200 transition-colors font-medium mb-2"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Optimization
        </button>
      </div>
      {/* Page Header */}
      <div className="mb-6 sm:mb-8">
        <div className="mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Pallet Visualization</h1>
          <p className="text-gray-600 mt-2">View your optimized pallet layouts</p>
        </div>
        {/* View Toggle */}
        <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 mb-6 sm:mb-8">
          <button
            onClick={() => setActiveView('visualization')}
            className={`flex items-center space-x-2 px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeView === 'visualization'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span>2D View</span>
          </button>
          <button
            onClick={() => setActiveView('details')}
            className={`flex items-center space-x-2 px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeView === 'details'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>Details</span>
          </button>
        </div>
        {/* Summary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 sm:mb-8">
          <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200 text-center">
            <div className="text-2xl sm:text-3xl font-bold text-blue-600">{result.totalPallets}</div>
            <div className="text-sm text-gray-600">Pallets</div>
          </div>
          <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200 text-center">
            <div className="text-2xl sm:text-3xl font-bold text-green-600">{result.efficiency.toFixed(1)}%</div>
            <div className="text-sm text-gray-600">Efficiency</div>
          </div>
          <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200 text-center">
            <div className="text-2xl sm:text-3xl font-bold text-purple-600">
              {result.pallets.reduce((sum, pallet) => 
                sum + pallet.layers.reduce((layerSum, layer) => layerSum + layer.boxes.length, 0), 0
              )}
            </div>
            <div className="text-sm text-gray-600">Total Boxes</div>
          </div>
        </div>
      </div>
      {/* Content */}
      <div>
        {activeView === 'visualization' ? (
          <PalletVisualization pallets={result.pallets} />
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <ResultsDisplay result={result} />
          </div>
        )}
      </div>
    </div>
  );
}; 